import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router';
import { ApiError, eventEndpoints, opponentEndpoints, promotionEndpoints } from '../api';
import { NotPermitted } from '../App';
import { OFFER_KINDS, PROMOTION_CHIPS, PROMOTION_ICONS, WEEKDAYS, gamePromotionEndpoints, iconLabel } from '../api-game-promotions';
import type { GamePromotion, PromotionOffer, PromotionRule, RuleInput, RulePreview } from '../api-game-promotions';
import { blankOffer, createOfferBody, dealKindLocked, editOfferBody, offerFormFrom } from '../offer-form';
import type { DealType, OfferFormState } from '../offer-form';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars, humanize } from './event/shared';

/**
 * Promotions (D-377): the library of theme nights and specials — Dollar
 * Monday, Two for Tuesday, Fireworks Friday — each built once, linked to its
 * games by hand or by a rule that stays live, and carrying the offers the fan
 * actually gets.
 *
 * This page took the address `/promotions` from the discount codes, which are
 * now Promo codes at `/promo-codes`. An old link to a promo code's page
 * (`/promotions/<its id>`) still arrives: an id that is not a library
 * promotion but is a promo code is forwarded there.
 */
export default function GamePromotionsPage() {
  const { id } = useParams();
  const can = usePermissions();
  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  return id ? <PromotionEditor id={id} /> : <PromotionLibrary />;
}

function PromotionLibrary() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', headline: '', icon: 'STAR', chip: 'PROMOTIONS' });
  const list = useQuery({ queryKey: ['game-promotions'], queryFn: gamePromotionEndpoints.list });
  const create = useMutation({
    mutationFn: () => gamePromotionEndpoints.create({ name: form.name.trim(), headline: form.headline.trim() || null, icon: form.icon, chip: form.chip }),
    onSuccess: () => {
      setCreating(false);
      setForm({ name: '', headline: '', icon: 'STAR', chip: 'PROMOTIONS' });
      void queryClient.invalidateQueries({ queryKey: ['game-promotions'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const editable = can('events.manage');
  const rows = list.data?.promotions ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title="Promotions"
        description="Theme nights and specials the team runs across a season. Build one once, link it to its games — by hand, or with a rule such as “every Monday game” that keeps itself up to date — and give it what the fan gets: a line of information, a giveaway, an automatic ticket deal or a concession special. Discount codes are on the Promo codes page."
        actions={editable && !creating ? <Button variant="primary" onClick={() => setCreating(true)}>New promotion</Button> : null}
      >
        {creating ? (
          <form
            className="grid gap-3 border-t border-line px-5 py-4 md:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Name" htmlFor="gp-name" hint="What staff and fans call it: Dollar Monday.">
              <input id="gp-name" className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={80} />
            </Field>
            <Field label="Headline" htmlFor="gp-headline" hint="Optional. What the schedule says, when it differs from the name: $1 hot dogs all game.">
              <input id="gp-headline" className={INPUT} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} maxLength={120} />
            </Field>
            <Field label="Icon" htmlFor="gp-icon">
              <select id="gp-icon" className={INPUT} value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}>
                {PROMOTION_ICONS.map((icon) => (
                  <option key={icon.code} value={icon.code}>
                    {icon.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Schedule chip" htmlFor="gp-chip" hint="Which filter chip it lights up on the schedule.">
              <select id="gp-chip" className={INPUT} value={form.chip} onChange={(e) => setForm({ ...form, chip: e.target.value })}>
                {PROMOTION_CHIPS.map((chip) => (
                  <option key={chip.code} value={chip.code}>
                    {chip.label}
                  </option>
                ))}
              </select>
            </Field>
            <div className="flex items-end gap-2">
              <Button type="submit" variant="primary" disabled={create.isPending || !form.name.trim()}>
                {create.isPending ? 'Saving…' : 'Create'}
              </Button>
              <Button type="button" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </Panel>

      <Panel title="The library">
        <Table headers={['Promotion', 'When', 'Offers', 'Games to come', 'Next', 'Shown', '']}>
          {rows.length === 0 ? (
            <EmptyRow columns={7}>No promotions yet. Make one, then link it to its games.</EmptyRow>
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm">
                  <Link to={`/promotions/${row.id}`} className="font-medium text-brand hover:underline">
                    {row.name}
                  </Link>
                  <span className="block text-xs text-ink-500">
                    {iconLabel(row.icon)} · {PROMOTION_CHIPS.find((chip) => chip.code === row.chip)?.label ?? row.chip}
                    {row.sponsor ? ` · presented by ${row.sponsor}` : ''}
                  </span>
                </td>
                <td className="px-5 py-3 text-sm">{row.schedule_text}</td>
                <td className="px-5 py-3 text-sm">
                  <span className="flex flex-wrap gap-1">
                    {row.offers.filter((offer) => offer.active).map((offer) => (
                      <Badge key={offer.id} tone={offer.offer_kind === 'TICKET_DEAL' || offer.offer_kind === 'CONCESSION_SPECIAL' ? 'accent' : 'neutral'}>
                        {offer.badge ?? humanize(offer.offer_kind)}
                      </Badge>
                    ))}
                  </span>
                </td>
                <td className="px-5 py-3 text-sm tabular">
                  {row.upcoming}
                  {row.excluded_games > 0 ? <span className="text-xs text-ink-500"> ({row.excluded_games} excluded)</span> : null}
                </td>
                <td className="px-5 py-3 text-sm">{row.next_starts_at ? formatWhen(row.next_starts_at) : '—'}</td>
                <td className="px-5 py-3 text-sm">
                  <Badge tone={row.active && row.is_public ? 'ok' : 'neutral'}>{!row.active ? 'Off' : row.is_public ? 'Public' : 'Staff only'}</Badge>
                </td>
                <td className="px-5 py-3 text-right text-sm">
                  <Link to={`/promotions/${row.id}`} className="text-brand hover:underline">
                    Open
                  </Link>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

type Tab = 'details' | 'offers' | 'games';

function PromotionEditor({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const can = usePermissions();
  const [tab, setTab] = useState<Tab>('details');
  const [error, setError] = useState<string | null>(null);
  const detail = useQuery({ queryKey: ['game-promotion', id], queryFn: () => gamePromotionEndpoints.get(id), retry: false });
  const missing = detail.error instanceof ApiError && detail.error.status === 404;
  // An old bookmark to a promo code's page (`/promotions/<id>`) goes to its new address.
  const promoCode = useQuery({ queryKey: ['promotion', id, 'legacy'], queryFn: () => promotionEndpoints.get(id), enabled: missing && can('promotions.manage'), retry: false });
  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['game-promotion', id] });
    void queryClient.invalidateQueries({ queryKey: ['game-promotions'] });
  };
  const onError = (cause: Error) => setError(cause.message);

  if (promoCode.data) return <Navigate to={`/promo-codes/${id}`} replace />;
  if (missing && (promoCode.isError || !can('promotions.manage'))) return <ErrorNote>No such promotion.</ErrorNote>;
  const data = detail.data;
  if (!data) return <p className="text-sm text-ink-500">Loading…</p>;
  const editable = can('events.manage');

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/promotions" className="text-sm text-brand hover:underline">
            All promotions
          </Link>
          <h1 className="mt-1 text-xl font-semibold">{data.promotion.name}</h1>
          <p className="text-sm text-ink-500">
            {data.schedule_text} · {data.games.filter((game) => !game.excluded).length} game(s)
          </p>
        </div>
        <nav className="flex gap-1" aria-label="Promotion sections">
          {(['details', 'offers', 'games'] as Tab[]).map((name) => (
            <Button key={name} variant={tab === name ? 'primary' : 'secondary'} onClick={() => setTab(name)} aria-pressed={tab === name}>
              {name === 'details' ? 'Details' : name === 'offers' ? `Offers (${data.offers.length})` : `Games (${data.games.filter((game) => !game.excluded).length})`}
            </Button>
          ))}
        </nav>
      </div>
      {tab === 'details' ? <DetailsTab promotion={data.promotion} editable={editable} onSaved={refresh} onError={onError} /> : null}
      {tab === 'offers' ? <OffersTab promotionId={id} offers={data.offers} editable={editable} onSaved={refresh} onError={onError} /> : null}
      {tab === 'games' ? <GamesTab promotionId={id} data={data} editable={editable} onSaved={refresh} onError={onError} /> : null}
    </div>
  );
}

function DetailsTab({ promotion, editable, onSaved, onError }: { promotion: GamePromotion; editable: boolean; onSaved: () => void; onError: (error: Error) => void }) {
  const [form, setForm] = useState(() => ({
    name: promotion.name,
    slug: promotion.slug,
    headline: promotion.headline ?? '',
    detail: promotion.detail ?? '',
    sponsor: promotion.sponsor ?? '',
    icon: promotion.icon,
    chip: promotion.chip,
    accent_hex: promotion.accent_hex ?? '',
    is_public: promotion.is_public === 1,
    active: promotion.active === 1,
    sort_order: String(promotion.sort_order),
  }));
  const save = useMutation({
    mutationFn: () =>
      gamePromotionEndpoints.update(promotion.id, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        headline: form.headline,
        detail: form.detail,
        sponsor: form.sponsor,
        icon: form.icon,
        chip: form.chip,
        accent_hex: form.accent_hex || null,
        is_public: form.is_public,
        active: form.active,
        sort_order: Number(form.sort_order || 0),
      }),
    onSuccess: onSaved,
    onError,
  });
  const upload = useMutation({ mutationFn: (file: File) => gamePromotionEndpoints.uploadImage(promotion.id, file), onSuccess: onSaved, onError });
  const removeImage = useMutation({ mutationFn: () => gamePromotionEndpoints.removeImage(promotion.id), onSuccess: onSaved, onError });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value });

  return (
    <Panel title="Details" description="What the schedule, the game page and the Promotions page show. A sponsor or detail can be changed for one game on the Games tab.">
      <form
        className="grid gap-3 border-t border-line px-5 py-4 md:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name" htmlFor="gp-e-name">
          <input id="gp-e-name" className={INPUT} value={form.name} onChange={set('name')} disabled={!editable} maxLength={80} required />
        </Field>
        <Field label="Web address" htmlFor="gp-e-slug" hint="The schedule's ?promotion= filter.">
          <input id="gp-e-slug" className={INPUT} value={form.slug} onChange={set('slug')} disabled={!editable} maxLength={60} pattern="[a-z0-9][a-z0-9-]*" />
        </Field>
        <Field label="Headline" htmlFor="gp-e-headline" hint="Empty: the name is the headline.">
          <input id="gp-e-headline" className={INPUT} value={form.headline} onChange={set('headline')} disabled={!editable} maxLength={120} />
        </Field>
        <Field label="Sponsor" htmlFor="gp-e-sponsor" hint="As the schedule prints it after “Presented by”.">
          <input id="gp-e-sponsor" className={INPUT} value={form.sponsor} onChange={set('sponsor')} disabled={!editable} maxLength={120} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Detail" htmlFor="gp-e-detail">
            <textarea id="gp-e-detail" className={`w-full ${INPUT}`} rows={2} value={form.detail} onChange={set('detail')} disabled={!editable} maxLength={500} />
          </Field>
        </div>
        <Field label="Icon" htmlFor="gp-e-icon">
          <select id="gp-e-icon" className={INPUT} value={form.icon} onChange={set('icon')} disabled={!editable}>
            {PROMOTION_ICONS.map((icon) => (
              <option key={icon.code} value={icon.code}>
                {icon.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Schedule chip" htmlFor="gp-e-chip">
          <select id="gp-e-chip" className={INPUT} value={form.chip} onChange={set('chip')} disabled={!editable}>
            {PROMOTION_CHIPS.map((chip) => (
              <option key={chip.code} value={chip.code}>
                {chip.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Accent color" htmlFor="gp-e-accent" hint="Optional: the card's stripe on the Promotions page.">
          <span className="flex items-center gap-2">
            <input id="gp-e-accent" type="color" value={form.accent_hex || '#0b1f3a'} onChange={set('accent_hex')} disabled={!editable} />
            {form.accent_hex ? (
              <Button type="button" onClick={() => setForm({ ...form, accent_hex: '' })} disabled={!editable}>
                No accent
              </Button>
            ) : (
              <span className="text-xs text-ink-500">None</span>
            )}
          </span>
        </Field>
        <Field label="Order in the library" htmlFor="gp-e-sort">
          <input id="gp-e-sort" type="number" className={`w-24 ${INPUT}`} value={form.sort_order} onChange={set('sort_order')} disabled={!editable} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} disabled={!editable} />
          Shown to the public
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} disabled={!editable} />
          Active (off: hidden everywhere, and its deals and specials stop)
        </label>
        <div className="flex items-center gap-3 md:col-span-2">
          {promotion.image_url ? <img src={promotion.image_url} alt="" className="h-16 w-16 rounded object-cover" /> : null}
          {editable ? (
            <>
              <Button
                type="button"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/png,image/jpeg,image/webp';
                  input.onchange = () => {
                    const file = input.files?.[0];
                    if (file) upload.mutate(file);
                  };
                  input.click();
                }}
              >
                {promotion.image_url ? 'Replace image' : 'Add an image'}
              </Button>
              {promotion.image_url ? (
                <Button type="button" onClick={() => removeImage.mutate()}>
                  Remove image
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
        {editable ? (
          <div>
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        ) : null}
      </form>
    </Panel>
  );
}

/** What an offer does, in a sentence. */
function describeOffer(offer: PromotionOffer): string {
  switch (offer.offer_kind) {
    case 'TICKET_DEAL':
      switch (offer.promotion_type) {
        case 'PERCENT_OFF':
          return `${(offer.discount_basis_points ?? 0) / 100}% off each ticket`;
        case 'AMOUNT_OFF':
          return `${dollars(offer.discount_amount_cents)} off ${offer.discount_scope === 'PER_ORDER' ? 'the order' : 'each ticket'}`;
        case 'FIXED_PRICE':
          return `${dollars(offer.fixed_price_cents)} tickets`;
        case 'BUY_X_GET_Y':
          return `Buy ${offer.buy_quantity}, get ${offer.get_quantity} at ${(offer.get_discount_basis_points ?? 0) / 100}% off`;
        default:
          return humanize(offer.promotion_type);
      }
    case 'CONCESSION_SPECIAL':
      return `${offer.product_name ?? 'Item'} at ${dollars(offer.product_price_cents)}`;
    case 'GIVEAWAY':
      return offer.giveaway_quantity ? `For the first ${offer.giveaway_quantity.toLocaleString('en-US')} fans` : 'Giveaway';
    default:
      return offer.detail ?? '';
  }
}

/**
 * The fields an offer of this kind has, for adding one and for editing one
 * (D-383). `kindLocked` is a deal used on an order: its type and scope stay
 * as they were, and the form says why.
 */
function OfferFields({ form, setForm, idPrefix, editing, kindLocked, usedOnOrders }: { form: OfferFormState; setForm: (next: OfferFormState) => void; idPrefix: string; editing: boolean; kindLocked: boolean; usedOnOrders: number }) {
  const id = (name: string) => `${idPrefix}-${name}`;
  const lockHint = kindLocked ? `Used on ${usedOnOrders.toLocaleString('en-US')} order${usedOnOrders === 1 ? '' : 's'}, so the kind of discount stays; remove this offer and add a new one to change it.` : undefined;
  return (
    <>
      <Field label="Badge" htmlFor={id('label')} hint="Short, for the schedule: 2 FOR 1. Empty: worked out from the offer.">
        <input id={id('label')} className={INPUT} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} maxLength={40} />
      </Field>
      <div className="md:col-span-2">
        <Field label="Detail" htmlFor={id('detail')}>
          <input id={id('detail')} className={`w-full ${INPUT}`} value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} maxLength={300} />
        </Field>
      </div>
      {form.offer_kind === 'GIVEAWAY' ? (
        <Field label="How many" htmlFor={id('count')} hint="First this many fans. Optional.">
          <input id={id('count')} type="number" min={1} className={`w-32 ${INPUT}`} value={form.giveaway_quantity} onChange={(e) => setForm({ ...form, giveaway_quantity: e.target.value })} />
        </Field>
      ) : null}
      {form.offer_kind === 'TICKET_DEAL' ? (
        <>
          <Field label="Deal" htmlFor={id('type')} hint={lockHint}>
            <select id={id('type')} className={INPUT} value={form.promotion_type} disabled={kindLocked} onChange={(e) => setForm({ ...form, promotion_type: e.target.value as DealType, amount: '' })}>
              <option value="BUY_X_GET_Y">Buy X, get Y (2 for 1)</option>
              <option value="PERCENT_OFF">Percentage off</option>
              <option value="AMOUNT_OFF">Amount off</option>
              <option value="FIXED_PRICE">Fixed price per ticket</option>
            </select>
          </Field>
          {form.promotion_type === 'BUY_X_GET_Y' ? (
            <>
              <Field label="Buy" htmlFor={id('buy')}>
                <input id={id('buy')} type="number" min={1} className={`w-20 ${INPUT}`} value={form.buy_quantity} onChange={(e) => setForm({ ...form, buy_quantity: e.target.value })} required />
              </Field>
              <Field label="Get" htmlFor={id('get')} hint="At this % off (100 is free).">
                <span className="flex gap-2">
                  <input id={id('get')} type="number" min={1} className={`w-20 ${INPUT}`} value={form.get_quantity} onChange={(e) => setForm({ ...form, get_quantity: e.target.value })} required />
                  <input aria-label="Percent off" type="number" min={1} max={100} className={`w-20 ${INPUT}`} value={form.get_percent} onChange={(e) => setForm({ ...form, get_percent: e.target.value })} required />
                </span>
              </Field>
            </>
          ) : (
            <>
              {form.promotion_type === 'AMOUNT_OFF' ? (
                <Field label="Off what" htmlFor={id('scope')}>
                  <select id={id('scope')} className={INPUT} value={form.discount_scope} disabled={kindLocked} onChange={(e) => setForm({ ...form, discount_scope: e.target.value as 'PER_TICKET' | 'PER_ORDER' })}>
                    <option value="PER_TICKET">Each ticket</option>
                    <option value="PER_ORDER">The whole order</option>
                  </select>
                </Field>
              ) : null}
              <Field label={form.promotion_type === 'PERCENT_OFF' ? 'Percent off' : form.promotion_type === 'FIXED_PRICE' ? 'Price per ticket ($)' : 'Dollars off'} htmlFor={id('amount')}>
                <input id={id('amount')} type="number" min={0} step={0.01} className={`w-28 ${INPUT}`} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
              </Field>
            </>
          )}
          <Field label="Ticket types" htmlFor={id('types')} hint="Codes, comma separated (ADULT, SENIOR). Empty: every single-game ticket.">
            <input id={id('types')} className={INPUT} value={form.ticket_type_codes} onChange={(e) => setForm({ ...form, ticket_type_codes: e.target.value })} />
          </Field>
          <Field label="Price zones" htmlFor={id('zones')} hint="Codes, comma separated (GA). Empty: every zone.">
            <input id={id('zones')} className={INPUT} value={form.price_zone_codes} onChange={(e) => setForm({ ...form, price_zone_codes: e.target.value })} />
          </Field>
          <Field label="At least / at most per order" htmlFor={id('min')} hint="Optional ticket counts.">
            <span className="flex gap-2">
              <input id={id('min')} type="number" min={1} placeholder="Min" className={`w-20 ${INPUT}`} value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: e.target.value })} />
              <input aria-label="At most per order" type="number" min={1} placeholder="Max" className={`w-20 ${INPUT}`} value={form.max_quantity_per_order} onChange={(e) => setForm({ ...form, max_quantity_per_order: e.target.value })} />
            </span>
          </Field>
          <Field label="Total ticket cap" htmlFor={id('cap')} hint={editing ? 'Optional. Cannot go below the tickets that have already had the deal.' : 'Optional. When it runs out the deal quietly stops applying.'}>
            <input id={id('cap')} type="number" min={1} className={`w-28 ${INPUT}`} value={form.usage_cap_total} onChange={(e) => setForm({ ...form, usage_cap_total: e.target.value })} />
          </Field>
          {editing ? null : (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.activate} onChange={(e) => setForm({ ...form, activate: e.target.checked })} />
              Active now
            </label>
          )}
        </>
      ) : null}
      {form.offer_kind === 'CONCESSION_SPECIAL' ? (
        <>
          <Field label="Item" htmlFor={id('item')} hint={editing ? 'As it prints on the receipt. Orders already placed keep the name and price they were sold at.' : 'As it prints on the receipt: Hot dog voucher.'}>
            <input id={id('item')} className={INPUT} value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} maxLength={80} required />
          </Field>
          <Field label="Price ($)" htmlFor={id('price')}>
            <input id={id('price')} type="number" min={0} step={0.01} className={`w-28 ${INPUT}`} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
          </Field>
          <Field label="Most per order / stock" htmlFor={id('max')} hint={editing ? 'Stock is optional, and cannot go below what has sold.' : 'Stock is optional.'}>
            <span className="flex gap-2">
              <input id={id('max')} type="number" min={1} className={`w-20 ${INPUT}`} value={form.max_per_order} onChange={(e) => setForm({ ...form, max_per_order: e.target.value })} />
              <input aria-label="Stock" type="number" min={0} placeholder="Stock" className={`w-24 ${INPUT}`} value={form.stock_limit} onChange={(e) => setForm({ ...form, stock_limit: e.target.value })} />
            </span>
          </Field>
          <div className="md:col-span-3">
            <Field label="Item description" htmlFor={id('item-description')} hint="Optional. Shown beside the item at checkout.">
              <input id={id('item-description')} className={`w-full ${INPUT}`} value={form.item_description} onChange={(e) => setForm({ ...form, item_description: e.target.value })} maxLength={400} />
            </Field>
          </div>
        </>
      ) : null}
      {form.offer_kind === 'TICKET_DEAL' || form.offer_kind === 'CONCESSION_SPECIAL' ? (
        <fieldset className="flex items-center gap-4 text-sm md:col-span-3">
          <legend className="sr-only">Where</legend>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.web} onChange={(e) => setForm({ ...form, web: e.target.checked })} />
            Online
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.box_office} onChange={(e) => setForm({ ...form, box_office: e.target.checked })} />
            At the window
          </label>
        </fieldset>
      ) : null}
    </>
  );
}

/** Edit one offer in place (D-383): the kind is fixed, everything the form shows is saved. */
function OfferEditor({ promotionId, offer, onDone, onError }: { promotionId: string; offer: PromotionOffer; onDone: () => void; onError: (error: Error) => void }) {
  const [form, setForm] = useState<OfferFormState>(() => offerFormFrom(offer));
  const save = useMutation({ mutationFn: () => gamePromotionEndpoints.updateOffer(promotionId, offer.id, editOfferBody(form)), onSuccess: onDone, onError });
  return (
    <form
      className="grid gap-3 md:grid-cols-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-ink-500 md:col-span-3">
        Editing {OFFER_KINDS.find((entry) => entry.code === offer.offer_kind)?.label.toLowerCase()}.{' '}
        {(offer.used_on_orders ?? 0) > 0 ? `It is on ${(offer.used_on_orders ?? 0).toLocaleString('en-US')} order${offer.used_on_orders === 1 ? '' : 's'}; those keep what they were charged, and changes apply from now on.` : 'Changes apply from now on.'}
      </p>
      <OfferFields form={form} setForm={setForm} idPrefix={`offer-${offer.id}`} editing kindLocked={dealKindLocked(offer)} usedOnOrders={offer.used_on_orders ?? 0} />
      <div className="flex gap-2 md:col-span-3">
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save offer'}
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function OffersTab({ promotionId, offers, editable, onSaved, onError }: { promotionId: string; offers: PromotionOffer[]; editable: boolean; onSaved: () => void; onError: (error: Error) => void }) {
  const can = usePermissions();
  const [form, setForm] = useState<OfferFormState>({ ...blankOffer });
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => gamePromotionEndpoints.addOffer(promotionId, createOfferBody(form)),
    onSuccess: () => {
      setAdding(false);
      setForm({ ...blankOffer });
      onSaved();
    },
    onError,
  });
  const update = useMutation({ mutationFn: (input: { offerId: string; patch: Record<string, unknown> }) => gamePromotionEndpoints.updateOffer(promotionId, input.offerId, input.patch), onSuccess: onSaved, onError });
  const remove = useMutation({ mutationFn: (offerId: string) => gamePromotionEndpoints.removeOffer(promotionId, offerId), onSuccess: onSaved, onError });
  const kind = OFFER_KINDS.find((entry) => entry.code === form.offer_kind)!;
  // What else a deal or a special needs, beyond events.manage (checked again by the API).
  const mayChange = (offer: PromotionOffer) => editable && (!offer.promotion_id || can('promotions.manage')) && (!offer.add_on_product_id || can('add_ons.manage'));
  const confirmRemove = (offer: PromotionOffer) => {
    const used = offer.used_on_orders ?? 0;
    const what = offer.offer_kind === 'TICKET_DEAL' ? 'The deal ends and stops applying at checkout.' : offer.offer_kind === 'CONCESSION_SPECIAL' ? 'The item stops being sold.' : 'It comes off the schedule and game pages.';
    const orders = used > 0 ? ` It is on ${used.toLocaleString('en-US')} order${used === 1 ? '' : 's'}: they keep what they were charged, and the reports keep counting them.` : '';
    return window.confirm(`Remove this offer? ${what}${orders} This cannot be undone.`);
  };

  return (
    <Panel
      title="Offers"
      description="What the fan gets. A promotion can carry several — a ticket deal and a concession special, say. An automatic ticket deal and a promo code never combine on one order: checkout applies whichever saves the fan more, and says so. Edit an offer at any time; orders already placed keep what they were charged."
      actions={editable && !adding ? <Button variant="primary" onClick={() => setAdding(true)}>Add an offer</Button> : null}
    >
      {adding ? (
        <form
          className="grid gap-3 border-t border-line px-5 py-4 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <div className="md:col-span-3">
            <Field label="Kind" htmlFor="offer-kind" hint={kind.hint}>
              <select id="offer-kind" className={INPUT} value={form.offer_kind} onChange={(e) => setForm({ ...form, offer_kind: e.target.value as PromotionOffer['offer_kind'] })}>
                {OFFER_KINDS.map((entry) => (
                  <option key={entry.code} value={entry.code} disabled={(entry.code === 'TICKET_DEAL' && !can('promotions.manage')) || (entry.code === 'CONCESSION_SPECIAL' && !can('add_ons.manage'))}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <OfferFields form={form} setForm={setForm} idPrefix="offer" editing={false} kindLocked={false} usedOnOrders={0} />
          <div className="flex gap-2 md:col-span-3">
            <Button type="submit" variant="primary" disabled={add.isPending}>
              {add.isPending ? 'Saving…' : 'Add offer'}
            </Button>
            <Button type="button" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      <Table headers={['Badge', 'Kind', 'What it is', 'State', '']}>
        {offers.length === 0 ? (
          <EmptyRow columns={5}>No offers: the promotion is shown on its games and changes nothing at checkout.</EmptyRow>
        ) : (
          offers.map((offer) =>
            editingId === offer.id ? (
              <tr key={offer.id} className="border-b border-line last:border-0">
                <td colSpan={5} className="px-5 py-4">
                  <OfferEditor
                    promotionId={promotionId}
                    offer={offer}
                    onDone={() => {
                      setEditingId(null);
                      onSaved();
                    }}
                    onError={onError}
                  />
                </td>
              </tr>
            ) : (
              <tr key={offer.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm font-semibold">{offer.badge ?? '—'}</td>
                <td className="px-5 py-3 text-sm">{OFFER_KINDS.find((entry) => entry.code === offer.offer_kind)?.label}</td>
                <td className="px-5 py-3 text-sm">
                  {describeOffer(offer)}
                  {offer.restrictions && (offer.restrictions.ticket_type_codes.length > 0 || offer.restrictions.price_zone_codes.length > 0 || offer.restrictions.channels.length > 0) ? (
                    <span className="block text-xs text-ink-500">
                      {[offer.restrictions.ticket_type_codes.join(', '), offer.restrictions.price_zone_codes.join(', '), offer.restrictions.channels.map(humanize).join(', ')].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                  {offer.detail && offer.offer_kind !== 'INFO' ? <span className="block text-xs text-ink-500">{offer.detail}</span> : null}
                  {(offer.used_on_orders ?? 0) > 0 ? <span className="block text-xs text-ink-500">On {(offer.used_on_orders ?? 0).toLocaleString('en-US')} order{offer.used_on_orders === 1 ? '' : 's'}</span> : null}
                </td>
                <td className="px-5 py-3 text-sm">
                  <Badge tone={offer.active && (offer.deal_status ?? 'ACTIVE') === 'ACTIVE' && (offer.product_active ?? 1) === 1 ? 'ok' : 'neutral'}>
                    {!offer.active ? 'Off' : offer.deal_status ? humanize(offer.deal_status) : (offer.product_active ?? 1) === 1 ? 'On' : 'Item off'}
                  </Badge>
                </td>
                <td className="space-x-2 px-5 py-3 text-right text-sm whitespace-nowrap">
                  {editable && offer.promotion_id && offer.active && offer.deal_status !== 'ACTIVE' && offer.deal_status !== 'ENDED' && can('promotions.manage') ? (
                    <Button onClick={() => update.mutate({ offerId: offer.id, patch: { deal: { status: 'ACTIVE' } } })}>Activate</Button>
                  ) : null}
                  {editable && offer.promotion_id && offer.deal_status === 'ACTIVE' && can('promotions.manage') ? <Button onClick={() => update.mutate({ offerId: offer.id, patch: { deal: { status: 'PAUSED' } } })}>Pause</Button> : null}
                  {editable ? (
                    <Button onClick={() => update.mutate({ offerId: offer.id, patch: { active: !offer.active, ...(offer.add_on_product_id && !offer.active ? { special: { active: true } } : {}) } })}>
                      {offer.active ? 'Switch off' : 'Switch on'}
                    </Button>
                  ) : null}
                  {mayChange(offer) ? <Button onClick={() => setEditingId(offer.id)}>Edit</Button> : null}
                  {mayChange(offer) ? (
                    <Button variant="danger" disabled={remove.isPending} onClick={() => (confirmRemove(offer) ? remove.mutate(offer.id) : undefined)}>
                      Remove
                    </Button>
                  ) : null}
                </td>
              </tr>
            ),
          )
        )}
      </Table>
    </Panel>
  );
}

const blankRule = { season_id: '', weekdays: [] as number[], from_date: '', to_date: '', opponent_id: '', category_id: '', exclude_playoff: true, exclude_if_necessary: true };

function GamesTab({
  promotionId,
  data,
  editable,
  onSaved,
  onError,
}: {
  promotionId: string;
  data: Awaited<ReturnType<typeof gamePromotionEndpoints.get>>;
  editable: boolean;
  onSaved: () => void;
  onError: (error: Error) => void;
}) {
  const seasons = useQuery({ queryKey: ['seasons', 'all'], queryFn: () => eventEndpoints.seasons() });
  const opponents = useQuery({ queryKey: ['opponents', 'all'], queryFn: () => opponentEndpoints.list(true) });
  const categories = useQuery({ queryKey: ['event-categories'], queryFn: () => eventEndpoints.categories() });
  const events = useQuery({ queryKey: ['events', 'all'], queryFn: () => eventEndpoints.list() });
  const [rule, setRule] = useState({ ...blankRule });
  const [preview, setPreview] = useState<RulePreview | null>(null);
  const [adding, setAdding] = useState('');
  useEffect(() => {
    if (!rule.season_id && seasons.data?.seasons[0]) setRule((current) => ({ ...current, season_id: seasons.data!.seasons[0]!.id }));
  }, [seasons.data, rule.season_id]);

  const ruleInput = (): RuleInput => ({
    season_id: rule.season_id,
    weekdays: rule.weekdays,
    from_date: rule.from_date || null,
    to_date: rule.to_date || null,
    opponent_id: rule.opponent_id || null,
    category_id: rule.category_id || null,
    exclude_playoff: rule.exclude_playoff,
    exclude_if_necessary: rule.exclude_if_necessary,
  });
  const addRule = useMutation({
    mutationFn: () => gamePromotionEndpoints.addRule(promotionId, ruleInput()),
    onSuccess: (result) => {
      setPreview(result.preview);
      setRule({ ...blankRule, season_id: rule.season_id });
      onSaved();
    },
    onError,
  });
  const deleteRule = useMutation({ mutationFn: (ruleId: string) => gamePromotionEndpoints.deleteRule(promotionId, ruleId), onSuccess: (result) => { setPreview(result.preview); onSaved(); }, onError });
  const showPreview = useMutation({ mutationFn: () => gamePromotionEndpoints.preview(promotionId), onSuccess: (result) => setPreview(result.preview), onError });
  const apply = useMutation({ mutationFn: () => gamePromotionEndpoints.apply(promotionId), onSuccess: () => { setPreview(null); onSaved(); }, onError });
  const exclude = useMutation({ mutationFn: (eventId: string) => gamePromotionEndpoints.exclude(promotionId, eventId), onSuccess: onSaved, onError });
  const include = useMutation({ mutationFn: (eventId: string) => gamePromotionEndpoints.include(promotionId, eventId), onSuccess: onSaved, onError });
  const feature = useMutation({ mutationFn: (eventId: string) => gamePromotionEndpoints.updateLink(promotionId, eventId, { featured: true }), onSuccess: onSaved, onError });
  const addGame = useMutation({ mutationFn: () => gamePromotionEndpoints.addGames(promotionId, [adding]), onSuccess: () => { setAdding(''); onSaved(); }, onError });

  const seasonName = (id: string) => seasons.data?.seasons.find((season) => season.id === id)?.name ?? 'A season';
  const describeRule = (row: PromotionRule) =>
    [
      seasonName(row.season_id),
      row.weekdays.length === 0 ? 'every game' : `every ${row.weekdays.map((day) => WEEKDAYS[day]).join(', ')}`,
      row.from_date || row.to_date ? `${row.from_date ?? '…'} to ${row.to_date ?? '…'}` : null,
      row.opponent_id ? `vs ${opponents.data?.opponents.find((o) => o.id === row.opponent_id)?.name ?? 'one team'}` : null,
      row.category_id ? `in ${categories.data?.categories.find((c) => c.id === row.category_id)?.name ?? 'a category'}` : null,
      row.exclude_playoff ? 'not playoff games' : null,
      row.exclude_if_necessary ? 'not “if necessary” games' : null,
    ]
      .filter(Boolean)
      .join(' · ');
  const linkedIds = new Set(data.games.map((game) => game.event_id));
  const addable = (events.data?.events ?? []).filter((event) => event.status !== 'ARCHIVED' && !linkedIds.has(event.id));
  const gameLine = (id: string) => {
    const game = preview?.games[id];
    return game ? `${game.local_date ?? 'No date'} · ${game.title}${game.opponent_name ? ` (vs ${game.opponent_name})` : ''}` : id;
  };

  return (
    <div className="space-y-4">
      <Panel title="Rules" description="A rule links every game it selects, and stays live: games built, added, rescheduled or made doubleheaders later are picked up, and a game moved off the day drops off. A game you exclude or add by hand below stays that way whatever the rules say.">
        <div className="space-y-3 border-t border-line px-5 py-4 text-sm">
          {data.rules.length === 0 ? <p className="text-ink-500">No rules: the games are linked by hand.</p> : null}
          <ul className="space-y-1">
            {data.rules.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3">
                <span>{describeRule(row)}</span>
                {editable ? <Button onClick={() => deleteRule.mutate(row.id)}>Delete rule</Button> : null}
              </li>
            ))}
          </ul>
          {editable ? (
            <form
              className="grid gap-3 rounded-lg border border-line p-3 md:grid-cols-3"
              onSubmit={(e) => {
                e.preventDefault();
                addRule.mutate();
              }}
            >
              <Field label="Season" htmlFor="rule-season">
                <select id="rule-season" className={INPUT} value={rule.season_id} onChange={(e) => setRule({ ...rule, season_id: e.target.value })} required>
                  {(seasons.data?.seasons ?? []).map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </select>
              </Field>
              <fieldset className="md:col-span-2">
                <legend className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Weekdays (none ticked: every game)</legend>
                <div className="mt-1 flex flex-wrap gap-3">
                  {WEEKDAYS.map((day, index) => (
                    <label key={day} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={rule.weekdays.includes(index)}
                        onChange={() => setRule({ ...rule, weekdays: rule.weekdays.includes(index) ? rule.weekdays.filter((d) => d !== index) : [...rule.weekdays, index].sort() })}
                      />
                      {day.slice(0, 3)}
                    </label>
                  ))}
                </div>
              </fieldset>
              <Field label="From / to" htmlFor="rule-from" hint="Optional dates.">
                <span className="flex gap-2">
                  <input id="rule-from" type="date" className={INPUT} value={rule.from_date} onChange={(e) => setRule({ ...rule, from_date: e.target.value })} />
                  <input aria-label="To" type="date" className={INPUT} value={rule.to_date} onChange={(e) => setRule({ ...rule, to_date: e.target.value })} />
                </span>
              </Field>
              <Field label="Opponent" htmlFor="rule-opponent" hint="Optional.">
                <select id="rule-opponent" className={INPUT} value={rule.opponent_id} onChange={(e) => setRule({ ...rule, opponent_id: e.target.value })}>
                  <option value="">Any team</option>
                  {(opponents.data?.opponents ?? []).filter((o) => !o.archived).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Category" htmlFor="rule-category" hint="Optional.">
                <select id="rule-category" className={INPUT} value={rule.category_id} onChange={(e) => setRule({ ...rule, category_id: e.target.value })}>
                  <option value="">Any</option>
                  {(categories.data?.categories ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={rule.exclude_playoff} onChange={(e) => setRule({ ...rule, exclude_playoff: e.target.checked })} />
                Leave out playoff games
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={rule.exclude_if_necessary} onChange={(e) => setRule({ ...rule, exclude_if_necessary: e.target.checked })} />
                Leave out “if necessary” games
              </label>
              <div>
                <Button type="submit" variant="primary" disabled={addRule.isPending || !rule.season_id}>
                  Save rule and preview
                </Button>
              </div>
            </form>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => showPreview.mutate()} disabled={showPreview.isPending}>
              Preview
            </Button>
            {editable ? (
              <Button variant="primary" onClick={() => apply.mutate()} disabled={apply.isPending}>
                {apply.isPending ? 'Applying…' : 'Apply the rules (re-apply)'}
              </Button>
            ) : null}
          </div>
          {preview ? (
            <div className="grid gap-3 rounded-lg bg-page p-3 md:grid-cols-3">
              <div>
                <p className="font-semibold">Would add ({preview.plan.add.length})</p>
                <ul className="text-xs">{preview.plan.add.map((id) => <li key={id}>{gameLine(id)}</li>)}</ul>
              </div>
              <div>
                <p className="font-semibold">Would take off ({preview.plan.remove.length})</p>
                <ul className="text-xs">{preview.plan.remove.map((id) => <li key={id}>{gameLine(id)}</li>)}</ul>
              </div>
              <div>
                <p className="font-semibold">Excluded by hand, stays off ({preview.plan.excluded.length})</p>
                <ul className="text-xs">{preview.plan.excluded.map((id) => <li key={id}>{gameLine(id)}</li>)}</ul>
              </div>
            </div>
          ) : null}
        </div>
      </Panel>

      <Panel title="Games" description="Every game this promotion is on. Taking off a game a rule selects excludes it, so the rule cannot put it back; a game added by hand stays until taken off.">
        {editable ? (
          <form
            className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (adding) addGame.mutate();
            }}
          >
            <Field label="Add a game by hand" htmlFor="add-game">
              <select id="add-game" className={`w-96 ${INPUT}`} value={adding} onChange={(e) => setAdding(e.target.value)}>
                <option value="">Choose a game…</option>
                {addable.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                    {(event as { first_starts_at?: string | null }).first_starts_at ? ` · ${formatWhen((event as { first_starts_at?: string | null }).first_starts_at ?? null)}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Button type="submit" disabled={!adding || addGame.isPending}>
              Add
            </Button>
          </form>
        ) : null}
        <Table headers={['When', 'Game', 'How', 'Featured', '']}>
          {data.games.length === 0 ? (
            <EmptyRow columns={5}>Not on any game yet.</EmptyRow>
          ) : (
            data.games.map((game) => (
              <tr key={game.event_id} className={`border-b border-line last:border-0 ${game.excluded ? 'text-ink-400' : ''}`}>
                <td className="px-5 py-3 text-sm">{game.starts_at ? formatWhen(game.starts_at) : '—'}</td>
                <td className="px-5 py-3 text-sm">
                  <Link to={`/events/${game.event_id}`} className="hover:underline">
                    {game.title}
                  </Link>
                  {game.sponsor_override !== null || game.detail_override !== null ? <span className="block text-xs text-ink-500">Its own sponsor or detail</span> : null}
                </td>
                <td className="px-5 py-3 text-sm">
                  {game.excluded ? <Badge tone="warn">Excluded by hand</Badge> : <Badge tone={game.source === 'RULE' ? 'accent' : 'neutral'}>{game.source === 'RULE' ? 'By rule' : 'By hand'}</Badge>}
                </td>
                <td className="px-5 py-3 text-sm">{game.featured ? 'Featured' : ''}</td>
                <td className="space-x-2 px-5 py-3 text-right text-sm whitespace-nowrap">
                  {editable && game.excluded ? <Button onClick={() => include.mutate(game.event_id)}>Put back</Button> : null}
                  {editable && !game.excluded && !game.featured ? <Button onClick={() => feature.mutate(game.event_id)}>Feature</Button> : null}
                  {editable && !game.excluded ? <Button onClick={() => exclude.mutate(game.event_id)}>{game.source === 'RULE' ? 'Exclude' : 'Take off'}</Button> : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
